import { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X } from 'lucide-react';
import './AIHeaderAction.css';
import { ICON_SIZE } from '../design-system';

const AIHeaderTargetContext = createContext(undefined);

/**
 * One app-shell host for the open and close controls of every AI panel.
 * Keeping the host outside the panel also avoids transformed panels changing
 * the control's containing block during narrow-screen presence motion.
 */
export function AIHeaderActionProvider({ children }) {
  const [target, setTarget] = useState(null);

  return (
    <AIHeaderTargetContext.Provider value={target}>
      {children}
      <div className="ai-header-action-slot" ref={setTarget} />
    </AIHeaderTargetContext.Provider>
  );
}

export function AIHeaderAction({
  expanded = false,
  onClick,
  label,
  panelId,
  className = '',
}) {
  const target = useContext(AIHeaderTargetContext);

  if (target === undefined) {
    throw new Error('AIHeaderAction requires AIHeaderActionProvider in the app shell.');
  }
  if (!target) return null;

  const Icon = expanded ? X : Sparkles;
  const handleClick = () => {
    onClick();
    if (expanded) {
      // Closing replaces this action with the opener in the same stable host.
      requestAnimationFrame(() => {
        target.querySelector('button')?.focus({ preventScroll: true });
      });
    }
  };

  return createPortal(
    <button
      type="button"
      className={`ai-panel-header-action ${className}`.trim()}
      onClick={handleClick}
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={panelId}
      data-tooltip={label}
    >
      {/* Icon-md to match the rds-icon-button peers in the top bar: they draw
          --rds-icon-md (18) via CSS regardless of the lucide size attr. */}
      <Icon size={ICON_SIZE.md} aria-hidden="true" />
    </button>,
    target,
  );
}
